import Konva from 'konva';
import { ExportedBoxMath } from './box-math';
import { ExportedUtils } from './utils';
import { ExportedBoxUtils } from './box-utils';
import { SNAP_DIST } from './global-constants';

export class ExportedBoxIso {
	public readonly verticalLineSizePxPerIn: number = 3336.6781189415597 / 211.0236;
	public readonly horizontalLineSizePxPerIn: number = 3107.632259716467 / 211.0236;

	public debugEnabled: boolean = false;
	public dimensionsElements: any[] = [];

	public exportedBoxMath: ExportedBoxMath;
	public exportedUtils: ExportedUtils;
	public exportedBoxUtils: ExportedBoxUtils;

	public constructor() {
		this.exportedBoxMath = new ExportedBoxMath();
		this.exportedUtils = new ExportedUtils();
		this.exportedBoxUtils = new ExportedBoxUtils();
	}

	public clearDimensions(): void {
		for (const e of this.dimensionsElements) {
			e.destroy();
		}

		this.dimensionsElements.length = 0;
	}

	public getDimensionsPoints(boxes) {
		const distanceFromBoxes = 50;

		let topMostBox = boxes[0];
		let bottomMostBox = boxes[0];
		let rightMostBox = boxes[0];
		let leftMostBox = boxes[0];

		for (let i = 1; i < boxes.length; i++) {
			const currentBox = boxes[i];

			if (topMostBox.attrs.y > currentBox.attrs.y) {
				topMostBox = currentBox;
			}

			if (bottomMostBox.attrs.y + bottomMostBox.attrs.height < currentBox.attrs.y + currentBox.attrs.height) {
				bottomMostBox = currentBox;
			}

			if (rightMostBox.attrs.x + rightMostBox.attrs.width < currentBox.attrs.x + currentBox.attrs.width) {
				rightMostBox = currentBox;
			} else {
				if (rightMostBox.attrs.x + rightMostBox.attrs.width === currentBox.attrs.x + currentBox.attrs.width
						&& rightMostBox.attrs.y < currentBox.attrs.y) {
					rightMostBox = currentBox
				}
			}

			if (leftMostBox.attrs.x > currentBox.attrs.x) {
				leftMostBox = currentBox;
			} else {
				if (leftMostBox.attrs.x === currentBox.attrs.x && leftMostBox.attrs.y < currentBox.attrs.y) {
					leftMostBox = currentBox;
				}
			}
		}

		const fa = this.exportedBoxMath.getIsoFa();

		const topMostCube = this.exportedBoxMath.getCubeCornersFromBox(topMostBox);
		const bottomMostCube = this.exportedBoxMath.getCubeCornersFromBox(bottomMostBox);
		const leftMostCube = this.exportedBoxMath.getCubeCornersFromBox(leftMostBox);
		const rightMostCube = this.exportedBoxMath.getCubeCornersFromBox(rightMostBox);

		const topMostCubeBackLineFb = topMostCube.tbr.y - fa * topMostCube.tbr.x;
		const bottomMostCubeBackLineFb = bottomMostCube.bbr.y - fa * bottomMostCube.bbr.x;
		const bottomMostCubeFrontLineFb = bottomMostCube.bfr.y - fa * bottomMostCube.bfr.x;

		const verticalLineStartX = rightMostBox.attrs.x + rightMostBox.attrs.width + distanceFromBoxes;
		const verticalLineStartY = fa * verticalLineStartX + topMostCubeBackLineFb;
		const verticalLineEndX = rightMostBox.attrs.x + rightMostBox.attrs.width + distanceFromBoxes;
		const verticalLineEndY = fa * verticalLineEndX + bottomMostCubeBackLineFb;

		const verticalLineSize = verticalLineEndY - verticalLineStartY;

		const verticalLineTextX = verticalLineStartX + distanceFromBoxes;
		const verticalLineTextY = verticalLineStartY + (verticalLineEndY - verticalLineStartY) / 2;
		const verticalLineTextIn = verticalLineSize / this.verticalLineSizePxPerIn;
		const verticalLineTextFtIn = this.exportedUtils.inToFtAndIn(verticalLineTextIn);

		const horizontalLineStartX = leftMostCube.bfl.x;
		const horizontalLineStartY = fa * horizontalLineStartX + bottomMostCubeFrontLineFb + distanceFromBoxes;
		const horizontalLineEndX = rightMostCube.bfr.x;
		const horizontalLineEndY = fa * horizontalLineEndX + bottomMostCubeFrontLineFb + distanceFromBoxes;

		const horizontalLineSize = this.exportedBoxMath.getDistanceFromPoint(horizontalLineStartX, horizontalLineStartY, horizontalLineEndX, horizontalLineEndY);

		const horizontalLineTextIn = horizontalLineSize / this.horizontalLineSizePxPerIn;
		const horizontalLineTextFtIn = this.exportedUtils.inToFtAndIn(horizontalLineTextIn);

		const horizontalLineTextX = horizontalLineStartX + (horizontalLineEndX - horizontalLineStartX) / 2 - 75;
		const horizontalLineTextY = fa * horizontalLineTextX + bottomMostCubeFrontLineFb + 2 * distanceFromBoxes;

		console.log(`h=${horizontalLineSize}, v=${verticalLineSize}`);

		return {
			topMostCube,
			bottomMostCube,
			leftMostCube,
			rightMostCube,
			verticalLineStartX,
			verticalLineStartY,
			verticalLineEndX,
			verticalLineEndY,
			verticalLineTextX,
			verticalLineTextY,
			verticalLineTextIn,
			verticalLineTextFtIn,
			horizontalLineStartX,
			horizontalLineStartY,
			horizontalLineEndX,
			horizontalLineEndY,
			horizontalLineTextX,
			horizontalLineTextY,
			horizontalLineTextIn,
			horizontalLineTextFtIn
		};
	}

	public drawDimensions(layer, boxes): void {
		this.clearDimensions();

		if (boxes.length <= 0) {
			return;
		}

		const data = this.getDimensionsPoints(boxes);

		if (this.debugEnabled) {
			this.dimensionsElements.push(new Konva.Line({
				points: [data.topMostCube.tbl.x, data.topMostCube.tbl.y, data.verticalLineStartX, data.verticalLineStartY],
				stroke: 'black',
				strokeWidth: 6,
				lineCap: 'round',
				lineJoin: 'round',
				dash: [10, 10],
			}));

			this.dimensionsElements.push(new Konva.Line({
				points: [data.bottomMostCube.bbr.x, data.bottomMostCube.bbr.y, data.verticalLineEndX, data.verticalLineEndY],
				stroke: 'black',
				strokeWidth: 6,
				lineCap: 'round',
				lineJoin: 'round',
				dash: [10, 10],
			}));
		}

		this.dimensionsElements.push(new Konva.Line({
			points: [data.verticalLineStartX, data.verticalLineStartY, data.verticalLineEndX, data.verticalLineEndY],
			stroke: 'black',
			strokeWidth: 6,
			lineCap: 'round',
			lineJoin: 'round',
		}));

		this.dimensionsElements.push(new Konva.Circle({
			x: data.verticalLineStartX,
			y: data.verticalLineStartY,
			radius: 12,
			fill: 'black'
		}));

		this.dimensionsElements.push(new Konva.Circle({
			x: data.verticalLineEndX,
			y: data.verticalLineEndY,
			radius: 12,
			fill: 'black'
		}));

		if (this.debugEnabled) {
			this.dimensionsElements.push(new Konva.Text({
				x: data.verticalLineTextX,
				y: data.verticalLineTextY,
				text: data.verticalLineTextIn.toFixed(1) + ' in',
				fontSize: 50,
				fontFamily: 'Calibri',
				fill: 'black'
			}));
		} else {
			this.dimensionsElements.push(new Konva.Text({
				x: data.verticalLineTextX,
				y: data.verticalLineTextY,
				text: (data.verticalLineTextFtIn.ft
								? data.verticalLineTextFtIn.ft.toFixed(0) + ' ft '
								: '')
						+ data.verticalLineTextFtIn.in.toFixed(1) + ' in',
				fontSize: 50,
				fontFamily: 'Calibri',
				fill: 'black'
			}));
		}

		if (this.debugEnabled) {
			var leftMostIsoLine = new Konva.Line({
				points: [data.leftMostCube.tfl.x, data.leftMostCube.tfl.y, data.horizontalLineStartX, data.horizontalLineStartY],
				stroke: 'black',
				strokeWidth: 6,
				lineCap: 'round',
				lineJoin: 'round',
				dash: [10, 10],
			});

			this.dimensionsElements.push(leftMostIsoLine);

			var rightMostIsoLine = new Konva.Line({
				points: [data.rightMostCube.tfr.x, data.rightMostCube.tfr.y, data.horizontalLineEndX, data.horizontalLineEndY],
				stroke: 'black',
				strokeWidth: 6,
				lineCap: 'round',
				lineJoin: 'round',
				dash: [10, 10],
			});

			this.dimensionsElements.push(rightMostIsoLine);
		}

		this.dimensionsElements.push(new Konva.Line({
			points: [data.horizontalLineStartX, data.horizontalLineStartY, data.horizontalLineEndX, data.horizontalLineEndY],
			stroke: 'black',
			strokeWidth: 6,
			lineCap: 'round',
			lineJoin: 'round',
		}));

		this.dimensionsElements.push(new Konva.Circle({
			x: data.horizontalLineStartX,
			y: data.horizontalLineStartY,
			radius: 12,
			fill: 'black'
		}));

		this.dimensionsElements.push(new Konva.Circle({
			x: data.horizontalLineEndX,
			y: data.horizontalLineEndY,
			radius: 12,
			fill: 'black'
		}));

		if (this.debugEnabled) {
			this.dimensionsElements.push(new Konva.Text({
				x: data.horizontalLineTextX,
				y: data.horizontalLineTextY,
				text: data.horizontalLineTextIn.toFixed(1) + ' in',
				fontSize: 50,
				fontFamily: 'Calibri',
				fill: 'black'
			}));
		} else {
			this.dimensionsElements.push(new Konva.Text({
				x: data.horizontalLineTextX,
				y: data.horizontalLineTextY,
				text: (data.horizontalLineTextFtIn.ft
								? data.horizontalLineTextFtIn.ft.toFixed(0) + ' ft '
								: '')
						+ data.horizontalLineTextFtIn.in.toFixed(1) + ' in',
				fontSize: 50,
				fontFamily: 'Calibri',
				fill: 'black'
			}));
		}

		for (const e of this.dimensionsElements) {
			layer.add(e);
		}
	}

	public getPositionAfterSnaps(currentBox, otherBox): { x: number, y: number } {
		const additionalSpanDist = 50;
		const isoFa = this.exportedBoxMath.getIsoFa();

		const otherCube = this.exportedBoxMath.getCubeCornersFromBox(otherBox);
		let currentCube = this.exportedBoxMath.getCubeCornersFromBox(currentBox);
		const currentCubeSize = this.exportedBoxUtils.getCubeSize(currentBox);
		const currentBoxWidth = currentBox.attrs.width;
		const currentBoxHeight = currentBox.attrs.height;
		let currentBoxX = currentBox.attrs.x;
		let currentBoxY = currentBox.attrs.y;
		const log = [
			`initial:${currentBoxX},${currentBoxY}`
		];

		// right line snap, from the right
		if ((currentCube.bfl.y + additionalSpanDist) > otherCube.tfr.y &&
				(currentCube.tfl.y - additionalSpanDist) < otherCube.bfr.y) {
			const xDist = currentCube.tfl.x - otherCube.tfr.x;

			if (-SNAP_DIST < xDist && xDist < SNAP_DIST) {
				currentBoxX = currentBoxX - xDist;

				currentCube = this.exportedBoxMath.getCubeCornersFromProps(currentCubeSize, currentBoxX, currentBoxWidth, currentBoxY, currentBoxHeight);

				log.push(`Moved current box (right line, from the right) to: ${currentBoxX},${currentBoxY}`);
			}
		}

		// right line snap, from the left
		if ((currentCube.bfr.y + additionalSpanDist) > otherCube.tfr.y &&
				(currentCube.tfr.y - additionalSpanDist) < otherCube.bfr.y) {
			const xDist = otherCube.tfr.x - currentCube.tfr.x;

			if (-SNAP_DIST < xDist && xDist < SNAP_DIST) {
				currentBoxX = currentBoxX + xDist;

				currentCube = this.exportedBoxMath.getCubeCornersFromProps(currentCubeSize, currentBoxX, currentBoxWidth, currentBoxY, currentBoxHeight);

				log.push(`Moved current box (right line, from the left) to: ${currentBoxX},${currentBoxY}`);
			}
		}

		// left line snap, from the left
		if ((currentCube.bfr.y + additionalSpanDist) > otherCube.tfl.y &&
				(currentCube.tfr.y - additionalSpanDist) < otherCube.bfl.y) {
			const xDist = otherCube.tfl.x - currentCube.tfr.x;

			if (-SNAP_DIST < xDist && xDist < SNAP_DIST) {
				currentBoxX = currentBoxX + xDist;

				currentCube = this.exportedBoxMath.getCubeCornersFromProps(currentCubeSize, currentBoxX, currentBoxWidth, currentBoxY, currentBoxHeight);

				log.push(`Moved current box (left line, from the left) to: ${currentBoxX},${currentBoxY}`);
			}
		}

		// left line snap, from the right - f1
		if ((currentCube.bfl.y + additionalSpanDist) > otherCube.tfl.y &&
				(currentCube.tfl.y - additionalSpanDist) < otherCube.bfl.y) {
			const cubesDist = Math.abs(currentCube.tfl.x - otherCube.tfl.x);

			if (cubesDist < SNAP_DIST) {
				currentBoxX = otherCube.bfl.x;

				currentCube = this.exportedBoxMath.getCubeCornersFromProps(currentCubeSize, currentBoxX, currentBoxWidth, currentBoxY, currentBoxHeight);

				log.push(`l->r: ${currentBoxX},${currentBoxY}`);
			}
		}

		// top line snap, from the top - f1
		if ((currentCube.bfr.x + additionalSpanDist) > otherCube.tfl.x &&
				(currentCube.bfl.x - additionalSpanDist) < otherCube.tfr.x) {
			const fb = otherCube.tfl.y - isoFa * otherCube.tfl.x;
			const currentCubePerpendicularDropY = isoFa * currentCube.bfl.x + fb;
			const currentToOtherDist = Math.abs(currentCubePerpendicularDropY - currentCube.bfl.y);

			if (currentToOtherDist < SNAP_DIST) {
				currentBoxY = isoFa * currentCube.bfl.x + fb - currentCube.bfl.dTop;

				currentCube = this.exportedBoxMath.getCubeCornersFromProps(currentCubeSize, currentBoxX, currentBoxWidth, currentBoxY, currentBoxHeight);

				log.push(`t->t: ${currentBoxX},${currentBoxY}`);
			}
		}

		// top line snap, from bottom
		if ((currentCube.tfr.x + additionalSpanDist) > otherCube.tfl.x &&
				(currentCube.tfl.x - additionalSpanDist) < otherCube.tfr.x) {
			const a = (otherCube.tfl.y - otherCube.tfr.y) / (otherCube.tfl.x - otherCube.tfr.x);
			const b = (otherCube.tfl.x * otherCube.tfr.y - otherCube.tfr.x * otherCube.tfl.y) / (otherCube.tfl.x - otherCube.tfr.x);

			const currentCubePerpendicularDropY = a * currentCube.tfl.x + b;

			const yDist = currentCube.tfl.y - currentCubePerpendicularDropY;

			if (-SNAP_DIST < yDist && yDist < SNAP_DIST) {
				currentBoxY = currentBoxY - yDist;

				currentCube = this.exportedBoxMath.getCubeCornersFromProps(currentCubeSize, currentBoxX, currentBoxWidth, currentBoxY, currentBoxHeight);

				log.push(`Moved current box (top line, from the bottom) to: ${currentBoxX},${currentBoxY}`);
			}
		}

		// bottom line snap, from the bottom
		if ((currentCube.tfr.x + additionalSpanDist) > otherCube.bfl.x &&
				(currentCube.tfl.x - additionalSpanDist) < otherCube.bfr.x) {
			const a = (otherCube.bfl.y - otherCube.bfr.y) / (otherCube.bfl.x - otherCube.bfr.x);
			const b = (otherCube.bfl.x * otherCube.bfr.y - otherCube.bfr.x * otherCube.bfl.y) / (otherCube.bfl.x - otherCube.bfr.x);

			const currentCubePerpendicularDropY = a * currentCube.tfl.x + b;

			const yDist = currentCube.tfl.y - currentCubePerpendicularDropY;

			if (-SNAP_DIST < yDist && yDist < SNAP_DIST) {
				currentBoxY = currentBoxY - yDist;

				currentCube = this.exportedBoxMath.getCubeCornersFromProps(currentCubeSize, currentBoxX, currentBoxWidth, currentBoxY, currentBoxHeight);

				log.push(`Moved current box (bottom line, from the bottom) to: ${currentBoxX},${currentBoxY}`);
			}
		}

		// bottom line snap, from the top
		if ((currentCube.bfr.x + additionalSpanDist) > otherCube.bfl.x &&
				(currentCube.bfl.x - additionalSpanDist) < otherCube.bfr.x) {
			const a = (otherCube.bfl.y - otherCube.bfr.y) / (otherCube.bfl.x - otherCube.bfr.x);
			const b = (otherCube.bfl.x * otherCube.bfr.y - otherCube.bfr.x * otherCube.bfl.y) / (otherCube.bfl.x - otherCube.bfr.x);

			const currentCubePerpendicularDropY = a * currentCube.bfl.x + b;

			const yDist = currentCubePerpendicularDropY - currentCube.bfl.y;

			if (-SNAP_DIST < yDist && yDist < SNAP_DIST) {
				currentBoxY = currentBoxY + yDist;

				currentCube = this.exportedBoxMath.getCubeCornersFromProps(currentCubeSize, currentBoxX, currentBoxWidth, currentBoxY, currentBoxHeight);

				log.push(`Moved current box (bottom line, from the top) to: ${currentBoxX},${currentBoxY}`);
			}
		}

		return {
			x: currentBoxX,
			y: currentBoxY
		};
	}

	public changeIsDebug(debug: boolean): void {
		this.debugEnabled = debug;
	}
}
